import { useEffect, useState, useRef } from "react";
import { Loader2, Upload, Search, Copy, Trash2, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { adminListMedia, adminListMediaFolders, adminUploadMedia, adminRenameMedia, adminDeleteMedia } from "../admin/adminApi";

export default function CmsMedia() {
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [renameId, setRenameId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const fileRef = useRef(null);

  const backend = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    setLoading(true);
    try {
      const [f, m] = await Promise.all([
        adminListMediaFolders(),
        adminListMedia({ folder: folder || undefined, search: search || undefined }),
      ]);
      setFolders(f);
      setItems(m);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [folder]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const targetFolder = folder || "General";
    try {
      for (const file of files) {
        const data = await fileToDataUrl(file);
        await adminUploadMedia({ name: file.name, data, folder: targetFolder });
      }
      toast.success(`${files.length} file(s) uploaded to ${targetFolder}`);
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const doSearch = (e) => { e.preventDefault(); load(); };
  const copyUrl = (url) => { navigator.clipboard.writeText(`${backend}${url}`); toast.success("URL copied"); };
  const startRename = (m) => { setRenameId(m.id); setRenameVal(m.name); };
  const commitRename = async () => {
    if (!renameId) return;
    try {
      await adminRenameMedia(renameId, { name: renameVal });
      setRenameId(null); await load();
      toast.success("Renamed");
    } catch { toast.error("Rename failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this image? Any place using it will break.")) return;
    try { await adminDeleteMedia(id); await load(); toast.success("Deleted"); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <div data-testid="cms-media">
      <div className="mb-8">
        <div className="text-[11px] tracking-[0.3em] uppercase text-[#14523d] font-semibold">Operations</div>
        <h1 className="font-serif text-4xl text-[#3b1a1a] mt-1">Media Library</h1>
        <p className="text-[#5a4b4b] mt-2">Upload, organise and manage all images used across your website.</p>
      </div>

      <div className="bg-white border border-[#e5dfd2] rounded-lg p-4 flex flex-wrap items-center gap-3 mb-6">
        <select value={folder} onChange={(e) => setFolder(e.target.value)} className="inp max-w-xs" data-testid="cms-media-folder">
          <option value="">All folders</option>
          {folders.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <form onSubmit={doSearch} className="flex items-center gap-2 flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 border border-[#e5dfd2] px-3 py-2 flex-1 rounded-md">
            <Search className="w-4 h-4 text-[#8a7c66]" />
            <input data-testid="cms-media-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search filename..." className="bg-transparent flex-1 outline-none text-sm" />
          </div>
        </form>
        <label className="inline-flex items-center gap-2 text-xs tracking-[0.24em] uppercase bg-[#14523d] hover:bg-[#0f3d2c] text-white px-5 py-3 cursor-pointer rounded-md">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={onUpload} className="hidden" data-testid="cms-media-upload" />
        </label>
      </div>

      {loading ? (
        <div className="grid place-items-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#14523d]" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-24 border-2 border-dashed border-[#e5dfd2] rounded-lg text-[#8a7c66]">No media yet. Upload files to get started.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((m) => (
            <div key={m.id} className="bg-white border border-[#e5dfd2] rounded-lg overflow-hidden group" data-testid={`cms-media-item-${m.id}`}>
              <div className="aspect-square bg-[#faf7ee] overflow-hidden">
                <img src={`${backend}${m.url}`} alt={m.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-3 space-y-2">
                {renameId === m.id ? (
                  <div className="flex items-center gap-1">
                    <input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} className="inp text-xs" />
                    <button onClick={commitRename} className="text-[#14523d]" data-testid="cms-media-rename-save"><Check className="w-4 h-4" /></button>
                    <button onClick={() => setRenameId(null)} className="text-[#7c1d1d]"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <div className="text-xs text-[#3b1a1a] truncate" title={m.name}>{m.name}</div>
                )}
                <div className="text-[10px] tracking-[0.18em] uppercase text-[#8a7c66]">{m.folder}</div>
                <div className="flex items-center gap-2 pt-1">
                  <button onClick={() => copyUrl(m.url)} title="Copy URL" className="text-xs text-[#14523d] flex items-center gap-1" data-testid={`cms-media-copy-${m.id}`}><Copy className="w-3.5 h-3.5" /></button>
                  <button onClick={() => startRename(m)} title="Rename" className="text-xs text-[#7c1d1d]" data-testid={`cms-media-rename-${m.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(m.id)} title="Delete" className="text-xs text-[#7c1d1d] ml-auto" data-testid={`cms-media-del-${m.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}
